<script setup lang="ts">
import { computed, type PropType } from 'vue';
import type { SidebarLinkModel } from './interfaces/SidebarLinkModel';
import { SidebarLinkIdentifiers } from './LinkIdentifiers';
import { useRoute } from 'vue-router';
import { routeConstants } from '@/utils';

const props = defineProps({
  link: {
    type: Object as PropType<SidebarLinkModel>,
    required: true
  },
  collapsed: {
    type: Boolean,
    required: false,
    default: false
  }
});

const route = useRoute();

// `link.route` is normally a route *name* (see routeBinding below), but a few links carry a
// path fragment instead (e.g. organizations/:id/billing). Names and paths are not always the
// same string — `studySettings` vs `study_settings`, `default-surgeon-dashboard` vs
// `surgeon-dashboard` — so a path-contains check alone never matches those. Try the name
// first, then fall back to the path for the path-style routes.
const isLinkActive = computed(() => {
  const target = props.link.route;
  // Nested links such as "New View" carry no route; '' would otherwise match every path.
  if (!target) {
    return false;
  }
  // Data stays highlighted while browsing the archive.
  if (props.link.id === SidebarLinkIdentifiers.DATA && route.name === routeConstants.ARCHIVE.name) {
    return true;
  }
  // Checking the whole matched chain keeps a parent highlighted on its nested pages.
  if (route.matched.some(record => record.name === target)) {
    return true;
  }
  return route.path.includes(target);
});

const emit = defineEmits(['linkClick']);

const linkClick = () => {
  emit('linkClick', props.link.id);
};

// Collapsed links keep their label in a tooltip, since the rail shows only the icon.
const tooltip = computed(() => (props.collapsed ? props.link.label : ''));

const isExternal = (linkRoute: string) => linkRoute.startsWith('http');

// Bind only the prop the rendered tag understands. Binding both left a dead `to`
// attribute on external anchors, and put the raw route *name* in the href of
// internal router-links (breaking their middle-click / open-in-new-tab URL).
const routeBinding = (linkRoute: string) =>
  (isExternal(linkRoute) ? { href: linkRoute } : { to: { name: linkRoute } });
</script>

<template>
  <div>
    <component :is="isExternal(link.route) ? 'a' : 'router-link'"
      v-bind="routeBinding(link.route)"
      :class="`sidebar-link ${isLinkActive ? 'router-link-active' : ''} ${collapsed ? 'collapsed-link' : ''}`"
      @click="linkClick"
      v-tooltip.right="tooltip"
      v-ripple>
      <FontAwesomeIcon :icon="[`${isLinkActive ? 'fas' : link.iconStyle}`, `${link.icon}`]"
        class="link-icon"/>
      <div class="link-text"
        :class="{ 'selected-link-text': link.selected }"
        v-if="!collapsed">
        {{ link.label }}
      </div>
    </component>

    <div v-for="nestedLink in link.nestedLinks" :key="nestedLink.id">
      <component :is="isExternal(nestedLink.route) ? 'a' : 'router-link'"
        class="sidebar-link nested-link"
        :class="{ 'collapsed-link': collapsed }"
        v-bind="routeBinding(nestedLink.route)"
        v-tooltip.right="collapsed ? nestedLink.label : ''"
        v-ripple>
        <FontAwesomeIcon :icon="[
            `${isLinkActive ? 'fas' : nestedLink.iconStyle}`,
            `${nestedLink.icon}`,
          ]"
          class="link-icon nested-link-icon"/>
        <div class="link-text nested-link-text" v-if="!collapsed">{{ nestedLink.label }}</div>
      </component>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../assets/variables.module' as *;
.sidebar-link {
  display: flex;
  // Inset by 8px so hover/active read as a rounded pill rather than a full-bleed band.
  // The padding drops by the same 8px to keep the icons on their original x position.
  margin: 2px 8px;
  padding: 11px 8px 11px 22px;
  align-items: center;
  text-align: left;
  justify-content: flex-start;
  text-decoration: none;
  color: black;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 8px;
  // Keeps the ripple from spilling out of the rounded corners.
  position: relative;
  overflow: hidden;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  &:hover {
    background-color: hsl(210, 20%, 96%);
  }
}
.nested-link {
  padding-left: 37px;
}
.collapsed-link {
  padding-left: 0;
  padding-right: 0;
  justify-content: center;
  &.nested-link {
    padding-left: 0;
  }
  .link-icon {
    margin-right: 0;
  }
}
.link-icon {
  color: $primary-color;
  font-size: 20px;
  width: 30px;
  margin-right: 16px;
}
.nested-link-icon {
  font-size: 18px;
  margin-right: 0px;
}
.link-text {
  padding: 0 16px;
  font-size: 16px;
  font-weight: 500;
}
.selected-link-text {
  font-weight: 700;
}
.nested-link-text {
  font-size: 14px;
}
// Selected state is the same rounded pill, tinted to match the blue link icons.
.router-link-active {
  background-color: hsl(248.5, 100%, 96%);
  border-color: hsl(248.5, 100%, 88%);
  &:hover {
    background-color: hsl(248.5, 100%, 93%);
  }
  .link-text {
    font-weight: 700;
  }
}
</style>
